import { Box, Grid, Skeleton, Typography } from "@mui/material";
import JobStatusFilter, { type JobStatus } from "@components/ui/dashboard/JobStatusFilter";
import JobCard, { type JobCardProps } from "@components/ui/dashboard/JobCard";
import { useCallback, useEffect, useRef, useState } from "react";
import { getOfferOverview } from "@services/api/offer/getOverview/getOverview";
import Pagination from "@components/common/Pagination";
import { useGetFormBuilderAccessToken } from "@hooks/useGetFormBuilderAccessToken";
import { changeOfferStatus } from "@services/api/offer/changeOfferStatus/changeOfferStatus";
import { useLanguage } from "../../contexts/LanguageContext";
import translations from "./JobList.translations.json";
import { getOfferMetrics } from "@services/api/offer/getMetrics/getMetrics";
type SupportedLanguage = keyof typeof translations;

const MAIN_DARK_BG = "#0b0f17";

const JobList = () => {
  const { language } = useLanguage();
  const content = translations[language as SupportedLanguage] || translations.en;
  const [jobOffers, setJobOffers] = useState<JobCardProps[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(7);
  const [statusFilter, setStatusFilter] = useState<JobStatus>("OPEN");

  // Ref pour annuler les requêtes HTTP obsolètes si les filtres changent rapidement
  const abortControllerRef = useRef<AbortController | null>(null);

  const { loading: loadingFormBuilderAccessToken, formBuilderAccessToken } =
    useGetFormBuilderAccessToken(["recruiter", "offer", "offer-assesment"]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  // Transformation d'une offre brute de l'API vers le type attendu par JobCard
  // Les compteurs proviennent des metrics (clé = offerId) retournées par /offer/metrics
  const mapOfferToJobCard = useCallback((offer: any, metrics?: any): JobCardProps => ({
    item: {
      id: offer.id,
      uuid: offer.uuid,
      title: offer.title,
      location: offer.location,
      status: offer.status,
      questionsCount: offer.questionsCount ?? 0,
      technicalQuestionsCount: offer.technicalQuestionsCount ?? 0,
      applicants: metrics?.applications ?? 0,
      shortlisted: metrics?.shortlisted ?? 0,
      answeredQuestions: metrics?.answeredQuestions ?? 0,
      assessmentRejected: metrics?.assessmentRejected ?? 0,
      assessmentValidated: metrics?.assessmentValidated ?? 0,
      technicalAssessmentRejected: metrics?.technicalAssessmentRejected ?? 0,
      technicalAssessmentValidated: metrics?.technicalAssessmentValidated ?? 0,
      underFinalReview: metrics?.underFinalReview ?? 0,
      acceptedAfterFinalReview: metrics?.acceptedAfterFinalReview ?? 0,
      rejectedAfterFinalReview: metrics?.rejectedAfterFinalReview ?? 0,
      offerSent: metrics?.offerSent ?? 0,
      rejectedByCandidate: metrics?.rejectedByCandidate ?? 0,
      acceptedByCandidate: metrics?.acceptedByCandidate ?? 0,
      contractSent: metrics?.contractSent ?? 0,
      hired: metrics?.hired ?? 0,
    },
  }), []);

  // Fonction centrale pour l'appel API getOfferOverview
  const fetchOfferOverview = useCallback(async () => {
    // Annule la requête précédente si elle est encore en cours
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);

    try {
      const response = await getOfferOverview(
        {
          page: currentPage,
          itemsPerPage: rowsPerPage,
          status: statusFilter,
        }
      );

      if (response?.data) {
        const ids = (response.data.offers || []).map((offer: any) => offer.id).join(",");
        if (ids ) {
          const metricsResponse = await getOfferMetrics({ ids });
          const metrics = metricsResponse?.data ?? {};
          const offers = (response.data.offers || []).map((offer: any) =>
            mapOfferToJobCard(offer, metrics[offer.id])
          );
          setJobOffers(offers);
          setTotalItems(response.data.totalItems || 0);
        } else {
          setJobOffers([]);
          setTotalItems(0);
        }
        
        
      }
    } catch (error: any) {
      if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
        console.error("Erreur lors de la récupération des offres :", error);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [currentPage, rowsPerPage, statusFilter, mapOfferToJobCard]);

  const handleChangeJobStatus = async (
    offerId: number,
    newStatus: "OPEN" | "CLOSED"
  ) => {
    setLoadingStatus(true);
    const response = await changeOfferStatus(offerId, newStatus);
    if (response?.success) {
      setJobOffers((prevJobOffers) =>
        prevJobOffers
          .map((job) =>
            job.item.id === offerId
              ? { ...job, item: { ...job.item, status: newStatus } }
              : job
          )
          .filter((job) => job.item.status === statusFilter)
      );
    }
    setLoadingStatus(false);
  };

  useEffect(() => {
    fetchOfferOverview();

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [fetchOfferOverview]);

  const isDataLoading = loading || loadingFormBuilderAccessToken;

  return (
    <Box sx={{ flexGrow: 1, padding: 0, bgcolor: MAIN_DARK_BG }}>
      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <JobStatusFilter
          value={statusFilter}
          onChange={(status) => {
            setStatusFilter(status);
            setCurrentPage(1);
          }}
        />
      </Box>

      <Grid container spacing={2.5} sx={{ padding: 0, bgcolor: MAIN_DARK_BG }}>
        {isDataLoading && jobOffers.length === 0 ? (
          // Affichage de plusieurs skeletons pour représenter les cartes en attente
          Array.from({ length: 3 }).map((_, idx) => (
            <Grid item key={idx} xs={12}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={160}
                sx={{ bgcolor: "#121824", borderRadius: "16px" }}
              />
            </Grid>
          ))
        ) : (
          <>
            {jobOffers.map((job) => (
              <Grid item key={job.item.id} xs={12}>
                <JobCard
                  item={job.item}
                  formBuilderAccessToken={formBuilderAccessToken || ""}
                  onRefetch={fetchOfferOverview}
                  onChangeStatus={handleChangeJobStatus}
                  loadingStatus={loadingStatus}
                />
              </Grid>
            ))}

            {jobOffers.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body1" align="center" sx={{ color: "#fff", py: 4 }}>
                  {content.empty}
                </Typography>
              </Grid>
            )}
          </>
        )}
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[7, 14, 28, 30, 31, 48]}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Box>
    </Box>
  );
};

export default JobList;